<template>
    <div class="language-picker">
        <a-popover placement="bottomLeft" overlayClassName="language-picker-popover" :visible="visible" trigger="click">
            <template #content>
                <div class="picker-content" ref="pickerContent">
                    <div class="picker-header">
                        <span>DİL SEÇİN</span>
                    </div>

                    <div class="search-wrapper">
                        <a-input-search v-model="search" placeholder="Search Item" size="large" />
                    </div>

                    <div class="languages-list">
                        <div
                            v-for="lang in filteredLanguages"
                            :key="lang.id"
                            class="language-item"
                            :class="{ selected: lang.id === value }"
                            @click="selectLanguage(lang.id)"
                        >
                            <div class="language-flag-wrapper">
                                <vue-country-flag
                                    v-if="lang.countryCode"
                                    :country="lang.countryCode"
                                    size="normal"
                                    class="language-flag"
                                />
                                <i v-else class="material-icons language-icon">{{ lang.icon || "language" }}</i>
                            </div>
                            <div class="language-info">
                                <div class="language-name">{{ lang.name }}</div>
                                <div class="language-description">{{ lang.description }}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </template>

            <div class="language-trigger" @click="togglePopover">
                <div class="trigger-content">
                    <div class="trigger-flag-wrapper">
                        <vue-country-flag
                            v-if="selectedLanguage && selectedLanguage.countryCode"
                            :country="selectedLanguage.countryCode"
                            size="normal"
                            class="trigger-flag"
                        />
                        <i v-else class="material-icons trigger-icon">{{ $_.get(selectedLanguage, "icon", icon) }}</i>
                    </div>
                    <span class="trigger-label">{{ $_.get(selectedLanguage, "name", title) }}</span>
                </div>
                <i class="material-icons trigger-arrow">{{ visible ? "arrow_drop_up" : "arrow_drop_down" }}</i>
            </div>
        </a-popover>
    </div>
</template>

<script>
import Vue from "vue";
import CountryFlag from "vue-country-flag";

export default Vue.extend({
    name: "LanguagePicker",
    components: {
        VueCountryFlag: CountryFlag,
    },
    props: {
        value: {
            type: String,
            required: true,
        },
        languages: {
            type: Array,
            required: true,
        },
        title: {
            type: String,
            default: "Select Language",
        },
        icon: {
            type: String,
            default: "translate",
        },
    },
    data() {
        return {
            visible: false,
            search: "",
            pickerId: `language-picker-${Math.random().toString(36).substr(2, 9)}`,
        };
    },
    computed: {
        selectedLanguage() {
            return this.languages.find((lang) => lang.id === this.value);
        },
        filteredLanguages() {
            const query = (this.search || "").trim().toLowerCase();
            if (!query) {
                return this.languages;
            }
            return this.languages.filter(
                (lang) => lang.name.toLowerCase().includes(query) || lang.description.toLowerCase().includes(query)
            );
        },
    },
    methods: {
        togglePopover() {
            this.visible = !this.visible;
        },
        selectLanguage(id) {
            this.$emit("input", id);
            this.$emit("change", id);
            this.visible = false;
            this.search = "";
        },
        handleClickOutside(e) {
            if (!this.$refs.pickerContent?.contains(e.target) && !e.target.closest(".language-trigger")) {
                if (this.visible) {
                    this.visible = false;
                }
            }
        },
        handleEscape(e) {
            if (e.key === "Escape" && this.visible) {
                this.visible = false;
            }
        },
    },
    mounted() {
        document.addEventListener("click", this.handleClickOutside);
        document.addEventListener("keydown", this.handleEscape);
    },
    beforeDestroy() {
        document.removeEventListener("click", this.handleClickOutside);
        document.removeEventListener("keydown", this.handleEscape);
    },
});
</script>

<style lang="scss" scoped>
.language-picker {
    display: inline-block;
    width: 100%;
    .normal-flag {
        margin: unset !important;
    }
}

.language-trigger {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 6px 12px;
    border: 1px solid var(--color-border-default);
    border-radius: 6px;
    background: var(--color-bg-default);
    cursor: pointer;
    transition: all 0.2s ease;
    min-height: 48px;

    &:hover {
        border-color: #1890ff;
    }

    .trigger-content {
        display: flex;
        align-items: center;
        gap: 12px;
        flex: 1;
        min-width: 0;
    }

    .trigger-flag-wrapper {
        width: 36px;
        height: 36px;
        flex-shrink: 0;
        display: flex;
        align-items: center;
        justify-content: center;
        border-radius: 50%;
        background: #f3f4f6;
        overflow: hidden;
    }

    .trigger-flag {
        width: 36px !important;
        height: 36px !important;
        border-radius: 50%;
        flex-shrink: 0;
        object-fit: cover;
    }

    .trigger-icon {
        font-size: 20px;
        color: var(--color-text-weak-default);
        flex-shrink: 0;
    }

    .trigger-label {
        font-size: 14px;
        font-weight: 500;
        color: var(--color-text-strong-default);
        white-space: nowrap;
        overflow: hidden;
        text-overflow: ellipsis;
    }

    .trigger-arrow {
        font-size: 24px;
        color: var(--color-text-weak-default);
        flex-shrink: 0;
        margin-left: 8px;
    }
}
</style>

<style lang="scss">
.language-picker-popover {
    .normal-flag {
        margin: unset !important;
    }
    .ant-popover-inner-content {
        padding: 0 !important;
        box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
        border: 1px solid var(--color-border-default);
        border-radius: 8px;
        overflow: hidden;
    }

    .ant-popover-arrow {
        display: none;
    }

    .picker-content {
        width: 460px;
        max-height: 600px;
        display: flex;
        flex-direction: column;
        background: var(--color-bg-default);
    }

    .picker-header {
        padding: 12px 16px;
        background: var(--color-bg-neutral-low-default);
        border-bottom: 1px solid var(--color-border-default);
        font-weight: 600;
        font-size: 11px;
        letter-spacing: 0.5px;
        color: var(--color-text-strong-default);
        text-align: center;
    }

    .search-wrapper {
        padding: 12px 16px;
        border-bottom: 1px solid var(--color-border-default);
        background: var(--color-bg-default);
    }

    .languages-list {
        overflow-y: auto;
        max-height: 500px;

        &::-webkit-scrollbar {
            width: 6px;
        }

        &::-webkit-scrollbar-track {
            background: transparent;
        }

        &::-webkit-scrollbar-thumb {
            background: #d1d5db;
            border-radius: 3px;

            &:hover {
                background: #9ca3af;
            }
        }
    }

    .language-item {
        display: flex;
        align-items: center;
        gap: 16px;
        padding: 12px 16px;
        cursor: pointer;
        transition: background-color 0.2s ease;
        border-bottom: 1px solid var(--color-border-default);

        &:last-child {
            border-bottom: none;
        }

        &:hover {
            background: var(--color-bg-hover);
        }

        &.selected {
            background: #e6f7ff;
        }

        .language-flag-wrapper {
            width: 44px;
            height: 44px;
            flex-shrink: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            background: #f3f4f6;
            overflow: hidden;

            .language-flag {
                width: 44px !important;
                height: 44px !important;
                border-radius: 50%;
                object-fit: cover;
                display: block;
                margin: 0 auto;
            }

            .language-icon {
                font-size: 24px;
                color: var(--color-text-weak-default);
            }
        }

        .language-info {
            flex: 1;
            min-width: 0;
            display: flex;
            flex-direction: column;
            gap: 2px;

            .language-name {
                font-size: 15px;
                font-weight: 500;
                color: var(--color-text-strong-default);
                line-height: 1.4;
            }

            .language-description {
                font-size: 12px;
                color: var(--color-text-weak-default);
                line-height: 1.3;
            }
        }
    }
}
</style>
